import React, { useState, useRef, useEffect } from "react";
import Layout from "../components/Layout";
import "../assets/css/AddCase.css"; // Can reuse the same CSS
import axios from "axios";
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

const EditCase = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Get the case ID from the URL

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // To show a loading state while fetching data
  const [caseData, setCaseData] = useState({
    year: "",
    caseType: "",
    caseNumber: "",
    grouping: "",
    petitionerName: "",
    petitionerAdvocate: "",
    petitionerAddress: "",
    respondentName: "",
    respondentAdvocate: "",
    respondentAddress: "",
    status: "",
  });

  const [existingFiles, setExistingFiles] = useState([]); // To store files already uploaded
  const [selectedFiles, setSelectedFiles] = useState([]); // To store new files to be uploaded
  const [filesToRemove, setFilesToRemove] = useState([]); // To track existing files marked for deletion
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch existing case data on component mount
  useEffect(() => {
    const fetchCase = async () => {
      if (!user?.token) {
        toast.error("Authentication required.");
        setIsLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/api/cases/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });

        console.log("API Response:", response.data); // Debug log

        if (response.data.status === "success") {
          const caseData = response.data.data.case; // Updated to match API structure
          console.log("Case Data:", caseData); // Debug log

          // Set the case data directly from the API response
          setCaseData({
            year: caseData.year,
            caseType: caseData.caseType,
            caseNumber: caseData.caseNumber,
            grouping: caseData.grouping,
            petitionerName: caseData.petitionerName,
            petitionerAdvocate: caseData.petitionerAdvocate,
            petitionerAddress: caseData.petitionerAddress,
            respondentName: caseData.respondentName,
            respondentAdvocate: caseData.respondentAdvocate,
            respondentAddress: caseData.respondentAddress,
            status: caseData.status,
          });

          // Set the files if they exist
          if (caseData.files && Array.isArray(caseData.files)) {
            setExistingFiles(caseData.files);
          }
        } else {
          toast.error(response.data.message || "Case not found.");
        }
      } catch (error) {
        console.error("Error fetching case:", error);
        toast.error("Failed to fetch case data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCase();
  }, [id, user?.token, BASE_URL]);

  // Add a useEffect to monitor caseData changes
  useEffect(() => {
    console.log("Current Case Data State:", caseData);
  }, [caseData]);

  // Validate form data (same as AddCase)
  const validateForm = () => {
    const errors = [];
    const currentYear = new Date().getFullYear();

    if (!caseData.year || caseData.year < 1900 || caseData.year > currentYear) {
      errors.push(`Year must be between 1900 and ${currentYear}`);
    }

    if (!caseData.caseNumber.match(/^[A-Za-z0-9-]+$/)) {
      errors.push('Case number can only contain letters, numbers, and hyphens');
    }

    const requiredFields = [
      'caseType', 'petitionerName', 'petitionerAdvocate',
      'respondentName', 'respondentAdvocate'
    ];
    requiredFields.forEach(field => {
      if (!caseData[field]?.trim()) {
        errors.push(`${field.replace(/([A-Z])/g, ' $1').trim()} is required`);
      }
    });
    return errors;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCaseData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle file selection for NEW files
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const totalFiles = existingFiles.length + selectedFiles.length + newFiles.length;

    if (totalFiles > 10) {
      toast.error('Maximum 10 files in total are allowed.');
      return;
    }

    const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error(`Some files exceed 5MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
    const invalidFiles = newFiles.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Only PDF and images are allowed.`);
      return;
    }
    
    // Prevent adding duplicates
    const allCurrentFileNames = [...existingFiles.map(f => f.name), ...selectedFiles.map(f => f.name)];
    const uniqueNewFiles = newFiles.filter(file => !allCurrentFileNames.includes(file.name));

    setSelectedFiles((prevFiles) => [...prevFiles, ...uniqueNewFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };
  
  // Handle removal of NEWLY selected files
  const handleRemoveNewFile = (fileNameToRemove) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileNameToRemove));
  };

  // Handle removal of EXISTING files
  const handleRemoveExistingFile = (fileToRemove) => {
    setExistingFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileToRemove.name));
    setFilesToRemove((prev) => [...prev, fileToRemove._id]); // Assuming file object has an _id
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);

    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      setIsSubmitting(false);
      return;
    }

    if (!user?.token) {
      toast.error("Authentication token not found. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    // Prepare form data for PUT request
    const formData = new FormData();
    Object.keys(caseData).forEach(key => {
      if (caseData[key] && key !== "_id" && key !== "files") { // Exclude fields that shouldn't be sent
        formData.append(key, caseData[key]);
      }
    });

    // Append new files
    selectedFiles.forEach((file) => {
      formData.append("files", file); // "files" for new files
    });

    // Append list of files to remove
    if (filesToRemove.length > 0) {
      formData.append("filesToRemove", JSON.stringify(filesToRemove));
    }

    try {
      // Use PUT method with the case ID
      const response = await axios.put(`${BASE_URL}/api/cases/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        timeout: 60000, // Increased timeout for larger uploads
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.status === "success") {
        toast.success("Case updated successfully!");
        navigate(`/cases/${id}`); // Navigate to the updated case's detail page
      } else {
        toast.error(response.data.message || "Failed to update case.");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred during update.";
      toast.error(errorMessage);
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show loading indicator while fetching initial data
  if (isLoading) {
    return <Layout><div>Loading case details...</div></Layout>;
  }

  const totalFileCount = existingFiles.length + selectedFiles.length;

  return (
    <Layout>
      <div className="add-case-container">
        <div className="form-wrapper">
          <h1 className="form-title">Edit Case</h1>
          <p className="form-subtitle">Update the details for case #{caseData.caseNumber}</p>

          <form onSubmit={handleSubmit}>
            {/* START: Identical Form Sections */}
            <div className="form-section">
              <h2 className="section-title">Case Details</h2>
              <div className="form-group">
                <label htmlFor="year">Year</label>
                <input
                  type="number"
                  id="year"
                  name="year"
                  value={caseData.year}
                  onChange={handleInputChange}
                  placeholder="Enter year"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="caseType">Case Type</label>
                <input
                  type="text"
                  id="caseType"
                  name="caseType"
                  value={caseData.caseType}
                  onChange={handleInputChange}
                  placeholder="Enter case type"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="caseNumber">Case Number</label>
                <input
                  type="text"
                  id="caseNumber"
                  name="caseNumber"
                  value={caseData.caseNumber}
                  onChange={handleInputChange}
                  placeholder="Enter case number"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="grouping">Grouping</label>
                <input
                  type="text"
                  id="grouping"
                  name="grouping"
                  value={caseData.grouping}
                  onChange={handleInputChange}
                  placeholder="Enter grouping"
                />
              </div>
              <div className="form-group">
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={caseData.date}
                  onChange={handleInputChange}
                />
              </div>
                {/* Status Field */}
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <input
                    type="text"
                    id="status"
                    name="status"
                    placeholder="Enter status (e.g., Open, Closed, Pending)"
                    value={caseData.status}
                    onChange={handleInputChange}
                    required
                  />
                </div>
            </div>
            <div className="form-grid">
              <div className="form-section">
                <h2 className="section-title">Petitioner's Details</h2>
                <div className="form-group">
                  <label htmlFor="petitionerName">Name</label>
                  <input
                    type="text"
                    id="petitionerName"
                    name="petitionerName"
                    value={caseData.petitionerName}
                    onChange={handleInputChange}
                    placeholder="Enter petitioner's name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="petitionerAdvocate">Advocate</label>
                  <input
                    type="text"
                    id="petitionerAdvocate"
                    name="petitionerAdvocate"
                    value={caseData.petitionerAdvocate}
                    onChange={handleInputChange}
                    placeholder="Enter petitioner's advocate"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="petitionerAddress">Address</label>
                  <textarea
                    id="petitionerAddress"
                    name="petitionerAddress"
                    value={caseData.petitionerAddress}
                    onChange={handleInputChange}
                    placeholder="Enter petitioner's address"
                    rows="3"
                  />
                </div>
              </div>
              <div className="form-section">
                <h2 className="section-title">Respondent's Details</h2>
                <div className="form-group">
                  <label htmlFor="respondentName">Name</label>
                  <input
                    type="text"
                    id="respondentName"
                    name="respondentName"
                    value={caseData.respondentName}
                    onChange={handleInputChange}
                    placeholder="Enter respondent's name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="respondentAdvocate">Advocate</label>
                  <input
                    type="text"
                    id="respondentAdvocate"
                    name="respondentAdvocate"
                    value={caseData.respondentAdvocate}
                    onChange={handleInputChange}
                    placeholder="Enter respondent's advocate"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="respondentAddress">Address</label>
                  <textarea
                    id="respondentAddress"
                    name="respondentAddress"
                    value={caseData.respondentAddress}
                    onChange={handleInputChange}
                    placeholder="Enter respondent's address"
                    rows="3"
                  />
                </div>
              </div>          
              
            </div>
            {/* END: Identical Form Sections */}
            
            {/* File Upload Section - Modified for Edit */}
            

            {/* Upload Progress Bar */}
            {isSubmitting && uploadProgress > 0 && (
              <div className="upload-progress">
                {/* Progress bar JSX is the same */}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? 'Updating Case...' : 'Update Case'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditCase;